import { proethicaModuleSummariesByModule } from "@constants/demo";
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useProethicaNextModule } from "@features/dashboard/hooks/useProethicaNextModule";
import { useSessionStore } from "@features/session";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import { SessionAnalysis, SessionAnalysisCard } from "@memorang/features";
import {
	Box,
	CardContainer,
	DialogWrapper,
	MarkdownRenderer,
	ScrollContainer,
	useDevice,
} from "@memorang/ui";
import { Image } from "expo-image";
import { router } from "expo-router";
import type { ComponentProps } from "react";
import { useMemo, useState } from "react";
import { ScrollView } from "react-native";
import { Button, Dialog, Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";

/* -----------------Types--------------- */
type IconName = keyof typeof Icon.glyphMap;

type OutlineSection = { title: string; items: string[] };

type ModuleLandingData = {
	module?: {
		label?: string;
		title?: string;
		badgeSource?: string | number | { uri: string };
		durationLabel?: string;
		activityCount?: number;
		knowledgeCheckQuestionCount?: number;
	};
	lesson?: { heading: string; text: string };
	outline?: { heading: string; sections: OutlineSection[] };
	learningObjectives?: { heading: string; intro: string; items: string[] };
};

type SessionAnalysisProps = Omit<
	ComponentProps<typeof SessionAnalysis>,
	| "cardTitle"
	| "cardSubtitle"
	| "cardIcon"
	| "cardContentHeight"
	| "cardTextLimit"
>;

type Props = {
	sessionTitle: string;
	sessionAnalysisProps: SessionAnalysisProps;
};

/* -----------------Helpers--------------- */
const findModuleLanding = (
	// biome-ignore lint/suspicious/noExplicitAny: demo session items are loosely typed
	items: any[],
): ModuleLandingData | undefined => {
	const landingItem = items?.find(
		(item) => item?.metadata?.taskType === "MODULE_LANDING",
	);
	return landingItem?.content?.moduleLanding as ModuleLandingData | undefined;
};

const capitalizeFirstLetter = (text: string) =>
	text.length ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;

const pluralize = (count: number, singular: string, plural: string) =>
	count === 1 ? singular : plural;

const NUMBER_WORDS: Record<string, number> = {
	one: 1,
	two: 2,
	three: 3,
	four: 4,
	five: 5,
	six: 6,
	seven: 7,
	eight: 8,
	nine: 9,
	ten: 10,
};

const toCount = (raw?: string): number | undefined => {
	if (!raw) {
		return undefined;
	}
	const lower = raw.toLowerCase();
	if (NUMBER_WORDS[lower] != null) {
		return NUMBER_WORDS[lower];
	}
	const parsed = Number.parseInt(lower, 10);
	return Number.isNaN(parsed) ? undefined : parsed;
};

const matchCount = (text: string, regex: RegExp): number | undefined =>
	toCount(text.match(regex)?.[1]);

const buildSessionSummary = (
	scenarioCount: number,
	activityCount?: number,
	knowledgeCheckCount?: number,
): string => {
	const parts: string[] = [];
	if (scenarioCount > 0) {
		parts.push(
			`${scenarioCount} ${pluralize(scenarioCount, "scenario", "scenarios")}`,
		);
	}
	if (activityCount) {
		parts.push(
			`${activityCount} ${pluralize(activityCount, "activity", "activities")}`,
		);
	}
	if (knowledgeCheckCount) {
		parts.push(`a ${knowledgeCheckCount}-question knowledge check`);
	}
	if (!parts.length) {
		return "";
	}
	const joined =
		parts.length > 1
			? `${parts.slice(0, -1).join(", ")}, and ${parts.at(-1)}`
			: parts[0];
	return `You completed ${joined} in this session.`;
};

/* -----------------Child components--------------- */
const PanelCard = ({
	icon,
	title,
	subtitle,
	children,
}: {
	icon: IconName;
	title: string;
	subtitle: string;
	children: React.ReactNode;
}) => (
	<CardContainer title={title} subtitle={subtitle} icon={icon}>
		<Box paddingHorizontal={16} paddingVertical={8}>
			{children}
		</Box>
	</CardContainer>
);

const LearnedRow = ({
	index,
	text,
	last,
}: {
	index: number;
	text: string;
	last: boolean;
}) => {
	const theme = useAppTheme();
	return (
		<Box
			flexDirection="row"
			alignItems="flex-start"
			paddingVertical={12}
			borderBottomWidth={last ? 0 : 1}
			borderBottomColor={theme.colors.outlineVariant}
			style={{ gap: 14 }}
		>
			<Box
				width={28}
				height={28}
				borderRadius={14}
				backgroundColor={theme.colors.secondaryContainer}
				alignItems="center"
				justifyContent="center"
				style={{ marginTop: 1 }}
			>
				<Text
					variant="labelMedium"
					style={{
						color: theme.colors.onSecondaryContainer,
						fontWeight: "800",
					}}
				>
					{index}
				</Text>
			</Box>
			<Box flex={1}>
				<MarkdownRenderer
					text={capitalizeFirstLetter(text)}
					fontSize={14}
					paragraphMarginVertical={0}
				/>
			</Box>
		</Box>
	);
};

const CoveredRow = ({
	icon,
	title,
	last,
}: {
	icon: IconName;
	title: string;
	last: boolean;
}) => {
	const theme = useAppTheme();
	return (
		<Box
			flexDirection="row"
			alignItems="center"
			paddingVertical={14}
			borderBottomWidth={last ? 0 : 1}
			borderBottomColor={theme.colors.outlineVariant}
			style={{ gap: 14 }}
		>
			<Icon
				name={icon}
				size={20}
				color={theme.colors.onSecondaryContainer}
				accessible={false}
			/>
			<Text
				variant="bodyMedium"
				style={{ color: theme.colors.onSurface, flex: 1 }}
			>
				{title}
			</Text>
		</Box>
	);
};

const ReportHeader = ({
	moduleLabel,
	badgeSource,
	title,
	summaryText,
}: {
	moduleLabel?: string;
	badgeSource?: string | number | { uri: string };
	title: string;
	summaryText: string;
}) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	return (
		<Box alignItems="center" paddingTop={8} style={{ gap: 8 }}>
			{badgeSource ? (
				<Image
					source={badgeSource}
					style={{ width: 80, height: 80 }}
					contentFit="contain"
					alt={moduleLabel ? `${moduleLabel} badge` : "Module badge"}
				/>
			) : (
				<Box
					width={72}
					height={72}
					borderRadius={36}
					alignItems="center"
					justifyContent="center"
					backgroundColor={theme.colors.success.main}
				>
					<Icon
						name="book-open-variant"
						size={32}
						color="#ffffff"
						accessible={false}
					/>
				</Box>
			)}
			{moduleLabel ? (
				<Text
					variant="labelLarge"
					style={{
						color: theme.colors.success.main,
						fontWeight: "800",
						letterSpacing: 1,
					}}
				>
					{`${moduleLabel.toUpperCase()} COMPLETE`}
				</Text>
			) : null}
			<Text
				variant={isMobile ? "headlineSmall" : "headlineMedium"}
				style={{
					color: theme.colors.onSurface,
					fontWeight: "800",
					textAlign: "center",
				}}
				accessibilityRole="header"
			>
				{title}
			</Text>
			{summaryText ? (
				<Text
					variant="bodyLarge"
					style={{
						color: theme.colors.onSurfaceVariant,
						textAlign: "center",
						maxWidth: 560,
					}}
				>
					{summaryText}
				</Text>
			) : null}
		</Box>
	);
};

const LearnedPanel = ({ objectives }: { objectives: string[] }) => {
	if (objectives.length === 0) {
		return null;
	}
	return (
		<PanelCard
			icon="bullseye-arrow"
			title="What you learned"
			subtitle="Learning objectives you worked toward in this module"
		>
			{objectives.map((objective, index) => (
				<LearnedRow
					key={objective}
					index={index + 1}
					text={objective}
					last={index === objectives.length - 1}
				/>
			))}
		</PanelCard>
	);
};

const CoveredPanel = ({ sections }: { sections: OutlineSection[] }) => {
	if (sections.length === 0) {
		return null;
	}
	return (
		<PanelCard
			icon="format-list-checks"
			title="What you covered"
			subtitle="Lessons and scenarios you completed in this module"
		>
			{sections.map((section, index) => (
				<CoveredRow
					key={section.title}
					icon={section.items.length > 0 ? "account-outline" : "scale-balance"}
					title={section.title}
					last={index === sections.length - 1}
				/>
			))}
		</PanelCard>
	);
};

const ReportFooter = ({
	continueLabel,
	loading,
	handleContinue,
	handleGoHome,
}: {
	continueLabel: string;
	loading: boolean;
	handleContinue: () => void;
	handleGoHome: () => void;
}) => {
	const { isMobile } = useDevice();
	const buttonHeight = 48;
	return (
		<Box
			flexDirection={isMobile ? "column" : "row"}
			alignItems="stretch"
			style={{ gap: 12 }}
		>
			<Box flex={isMobile ? undefined : 1}>
				<Button
					mode="contained"
					loading={loading}
					disabled={loading}
					onPress={handleContinue}
					contentStyle={{ height: buttonHeight }}
				>
					{continueLabel}
				</Button>
			</Box>
			<Button
				mode="outlined"
				icon="home-outline"
				onPress={handleGoHome}
				contentStyle={{ height: buttonHeight }}
			>
				Go home
			</Button>
		</Box>
	);
};

const ModuleSummaryDialog = ({
	visible,
	text,
	handleClose,
}: {
	visible: boolean;
	text: string;
	handleClose: () => void;
}) => (
	<DialogWrapper
		visible={visible}
		handleClose={handleClose}
		minHeight="60%"
		width="100%"
	>
		<Dialog.Icon
			icon={(props) => (
				<Icon
					{...props}
					name="book-open-variant"
					size={20}
					accessibilityLabel="Module summary"
				/>
			)}
		/>
		<Dialog.Title
			style={{
				textAlign: "center",
				marginBottom: 0,
			}}
		>
			Module summary
		</Dialog.Title>
		<Dialog.ScrollArea
			style={{
				paddingHorizontal: 0,
			}}
		>
			<ScrollView
				contentContainerStyle={{
					paddingBottom: 24,
					paddingHorizontal: 24,
					paddingTop: 8,
				}}
			>
				<MarkdownRenderer
					variant="explanation"
					text={text}
					paragraphMarginVertical={6}
				/>
			</ScrollView>
		</Dialog.ScrollArea>
		<Dialog.Actions>
			<Button onPress={handleClose}>Close</Button>
		</Dialog.Actions>
	</DialogWrapper>
);

/* -----------------Main--------------- */
const ProethicaReportView = ({ sessionTitle, sessionAnalysisProps }: Props) => {
	const { isMobile } = useDevice();
	const insets = useSafeAreaInsets();
	const currentModuleId = useDemoExamStore((state) => state.currentModuleId);
	const { nextModuleLabel, handleContinueToNextModule, isStartingNextModule } =
		useProethicaNextModule(currentModuleId);
	const [showSummaryDialog, setShowSummaryDialog] = useState(false);

	const moduleSummary = currentModuleId
		? proethicaModuleSummariesByModule[currentModuleId]
		: undefined;

	const sessionItems = useSessionStore((state) => state.sessionItems);
	const moduleLanding = useMemo(
		() => findModuleLanding(sessionItems),
		[sessionItems],
	);

	const title = moduleLanding?.module?.title ?? sessionTitle;
	const objectives = moduleLanding?.learningObjectives?.items ?? [];
	const sections = moduleLanding?.outline?.sections ?? [];
	const lessonText = moduleLanding?.lesson?.text ?? "";

	const scenarioCount = sections.filter(
		(section) => section.items.length > 0,
	).length;
	const activityCount =
		moduleLanding?.module?.activityCount ??
		matchCount(lessonText, /(\w+)\s+activit/i);
	const knowledgeCheckCount =
		moduleLanding?.module?.knowledgeCheckQuestionCount ??
		matchCount(lessonText, /(\w+)[-\s]question/i);
	const summaryText = buildSessionSummary(
		scenarioCount,
		activityCount,
		knowledgeCheckCount,
	);

	return (
		<ScrollContainer
			contentContainerStyle={{
				paddingBottom: 32 + insets.bottom,
				paddingHorizontal: 16,
			}}
		>
			<Box width="100%" maxWidth={1040} alignSelf="center" style={{ gap: 24 }}>
				<ReportHeader
					moduleLabel={moduleLanding?.module?.label}
					badgeSource={moduleLanding?.module?.badgeSource}
					title={title}
					summaryText={summaryText}
				/>

				<Box
					flexDirection={isMobile ? "column" : "row"}
					alignItems="flex-start"
					style={{ gap: 16 }}
				>
					<Box
						flex={isMobile ? undefined : 1}
						width={isMobile ? "100%" : undefined}
						style={{ gap: 16 }}
					>
						<LearnedPanel objectives={objectives} />
						<CoveredPanel sections={sections} />
					</Box>

					<Box
						flex={isMobile ? undefined : 1}
						width={isMobile ? "100%" : undefined}
						style={{ gap: 16 }}
					>
						{moduleSummary && (
							<>
								<SessionAnalysisCard
									sessionAnalysis={moduleSummary.text}
									title="Module summary"
									subtitle="Key concepts and takeaways from this module"
									icon="book-open-variant"
									contentHeight={220}
									textLimit={620}
									actionLabel="View more"
									toggleShowCompleteReport={() => setShowSummaryDialog(true)}
								/>
								<ModuleSummaryDialog
									visible={showSummaryDialog}
									text={moduleSummary.text}
									handleClose={() => setShowSummaryDialog(false)}
								/>
							</>
						)}
						<SessionAnalysis
							{...sessionAnalysisProps}
							cardTitle="Your personalized feedback"
							cardSubtitle="Tailored to how you worked through this module"
							cardIcon="lightbulb-on-outline"
							cardContentHeight={220}
							cardTextLimit={450}
						/>
					</Box>
				</Box>

				<ReportFooter
					continueLabel={
						nextModuleLabel ? `Continue to ${nextModuleLabel}` : "Continue"
					}
					loading={isStartingNextModule}
					handleContinue={handleContinueToNextModule}
					handleGoHome={() => router.replace("/(protected)/(tabs)/home")}
				/>
			</Box>
		</ScrollContainer>
	);
};

export default ProethicaReportView;
